import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useFetch } from "@/lib/useFetch";
import { usePageMeta } from "@/lib/usePageMeta";
import { useI18n } from "@/contexts/I18nContext";
import { GENRES } from "@/lib/constants";
import { ListLayout, FilterChip } from "@/components/ListLayout";
import { ListPagination } from "@/components/ListPagination";
import { ErrorState } from "@/components/StateViews";

const PAGE_SIZE = 24;

/** Square mark: the logo, or the publisher's initial. */
const PublisherMark = ({ publisher }) => (
  <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-[var(--radius)] border border-border bg-card">
    {publisher.logo
      ? <img src={publisher.logo} alt="" width="56" height="56" loading="lazy" className="h-full w-full object-cover" />
      : <span aria-hidden="true" className="font-display text-2xl text-primary">{[...(publisher.name || "?")][0]}</span>}
  </div>
);

export default function Publishers() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const genre = params.get("genre") || "";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);

  const qs = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
  if (genre) qs.set("genre", genre);
  const { status, data, reload } = useFetch(`/publishers?${qs}`);
  const items = data?.items || [];
  const total = data?.total || 0;
  usePageMeta({ title: t("all_publishers"), canonical: page > 1 ? `/publishers?page=${page}` : "/publishers" });
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [genre, page]);

  const set = (next) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) { if (v) p.set(k, v); else p.delete(k); }
    if (!("page" in next)) p.delete("page");
    setParams(p);
  };

  const filters = (
    <fieldset>
      <legend className="mb-2 font-semibold">{t("filter_genre")}</legend>
      <ul className="flex flex-wrap gap-2 md:flex-col md:gap-0.5">
        <li><FilterChip active={!genre} onClick={() => set({ genre: "" })} testid="publisher-genre-all">{t("all")}</FilterChip></li>
        {GENRES.map((g) => (
          <li key={g}><FilterChip active={genre === g} onClick={() => set({ genre: g })} testid={`publisher-genre-${g}`}>{g}</FilterChip></li>
        ))}
      </ul>
    </fieldset>
  );

  return (
    <ListLayout
      title={t("all_publishers")}
      count={status === "ok" ? t("count_publishers").replace("{n}", total) : null}
      filters={filters}
      active={genre ? [{ key: "genre", label: genre }] : []} onRemove={() => set({ genre: "" })}
    >
      {status === "error" ? <ErrorState onRetry={reload} />
        : status === "ok" && items.length === 0 ? <p className="py-10 text-muted-foreground">{t("no_publishers")}</p>
        : (
          <ul className="grid gap-x-8 gap-y-2 sm:grid-cols-2" data-testid="publishers-grid">
            {status !== "ok"
              ? Array.from({ length: 6 }, (_, i) => <li key={i}><div className="h-20 skel" /></li>)
              : items.map((p) => (
                <li key={p.id} className="border-b border-border">
                  <Link to={`/publishers/${p.slug}`} className="group flex items-center gap-4 py-3" data-testid={`publisher-${p.slug}`}>
                    <PublisherMark publisher={p} />
                    <span className="min-w-0">
                      <span className="block leading-snug break-words group-hover:text-primary">{p.name}</span>
                      {p.description && <span className="block text-sm text-muted-foreground line-clamp-1">{p.description}</span>}
                    </span>
                  </Link>
                </li>
              ))}
          </ul>
        )}
      {status === "ok" && <ListPagination page={page} limit={PAGE_SIZE} total={total} onPageChange={(p) => set({ page: p > 1 ? String(p) : "" })} testid="publishers-pagination" />}
    </ListLayout>
  );
}
